/* eslint-disable @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values */
/** @jsx jsx */
/** @jsxFrag */
import type { FC } from 'react';
import React from 'react';
import type { ReactComponentLike } from 'prop-types';
import { styled, css, jsx } from '@compiled/react'; // eslint-disable-line @typescript-eslint/no-unused-vars

import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import { SpotlightManager, SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import {
	akEditorFullWidthLayoutWidth,
	akEditorMaxWidthLayoutWidth,
} from '@atlaskit/editor-shared-styles';

import { VIEW_PAGE_TITLE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import {
	useRouteDataRef,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title/entry-points/EmojiComponent.tsx';
import {
	EMOJI_SIZE_LARGE,
	EMOJI_SIZE_MEDIUM,
} from '@confluence/emoji-title/entry-points/emojiTitleConstants.ts';
import {
	TitleAlignmentType,
	type TitleContentPropertiesType,
	COVERPICTUREWIDTH,
} from '@confluence/content-topper/entry-points/contentTopperConstants.ts';
import { useTitleContentPropertiesForPublishedPage } from '@confluence/content-topper/entry-points/useTitleContentPropertiesForPublishedPage.ts';
import { CopyLinkButtonLoader } from '@confluence/share/entry-points/CopyLink.tsx';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	RedactionsTourSteps,
	RedactionTourStepSource,
	REDACTIONS_TOUR_TARGETS,
	useRedactionsTour,
	useTrackTitleRedactionRendered,
} from '@confluence/redactions/entry-points/tour.ts';

import { CONTENT_TITLE_METRIC } from '../perf.config.js';

const EMOJI_TOP_OFFSET_OBJECT_HEADER_WITH_COVER_IMG = '-40px';
const EMOJI_TOP_OFFSET_COVER_IMG_ONLY = '-75px';
const EMOJI_TOP_OFFSET_DEFAULT = 'unset';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const HeadingWrapper = styled.div<{
	hasBanner?: boolean;
	isEmbeddedPage?: boolean;
}>({
	position: 'relative',
	marginBottom: ({ hasBanner, isEmbeddedPage }) => {
		if (isEmbeddedPage) {
			return 'initial';
		}
		return hasBanner ? token('space.300') : 'initial';
	},
});

// TODO: Refactor to a common location
// See https://product-fabric.atlassian.net/browse/CFE-2847

/* please update next/packages/content-title/src/ContentTitlePlaceholder.js if you change anything here */
const styledContentTitle = css({
	display: 'inline-block',
	boxSizing: 'border-box',
	minHeight: '120px',
	width: '100%',
	maxWidth: '100%',
});

const centerTitleStyles = css({
	display: 'block',
	margin: '0 auto',
	wordBreak: 'break-word',
	wordWrap: 'break-word',
	maxWidth: '760px',
});

const fullWidthLeftAlignStyles = css({
	margin: '0 auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- necessary to maintain parity/alignment with editor full-width
	maxWidth: `${akEditorFullWidthLayoutWidth}px`,
});

const maxWidthLeftAlignStyles = css({
	margin: '0 auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- necessary to maintain parity/alignment with editor full-width
	maxWidth: `${akEditorMaxWidthLayoutWidth}px`,
});

// Matches the 72px full-page gutter applied to the renderer in embedded view
// when the cc_page_experiences_native_embed_ui_updates experiment is enabled.
const embeddedNativeUITitlePaddingStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-space
	paddingLeft: '72px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-space
	paddingRight: '72px',
});

const getMarginTopStyledH1 = (isEmbeddedPage, hasCoverPicture, isObjectHeaderEnabled, hasEmoji) => {
	if (isObjectHeaderEnabled) {
		return isEmbeddedPage || (hasCoverPicture && hasEmoji) ? '0px' : '72px';
	}

	return isEmbeddedPage && hasCoverPicture ? token('space.negative.300') : '0px';
};

const getEmojiTopOffsetValue = (hasCoverPicture, isObjectHeaderEnabled) => {
	if (hasCoverPicture) {
		return isObjectHeaderEnabled
			? EMOJI_TOP_OFFSET_OBJECT_HEADER_WITH_COVER_IMG
			: EMOJI_TOP_OFFSET_COVER_IMG_ONLY;
	} else {
		return EMOJI_TOP_OFFSET_DEFAULT;
	}
};

// Note: this component extracts the props that we don't want to send to the DOM
// This avoids styled errors about these props being passed to the DOM
/* eslint-disable @typescript-eslint/no-unused-vars */
const ExperimentalHeadingContainer = ({
	children,
	isTitleCenterAligned,
	hasCoverPicture,
	isPageContentFullWidth,
	isPageContentMaxWidth,
	isEmbeddedPage,
	hasEmoji,
	isFixedWidthImageOption,
	isObjectHeaderEnabled,
	...props
}) => {
	return <div {...props}>{children}</div>;
};
/* eslint-enable @typescript-eslint/no-unused-vars */

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const StyledHeadingContainer = styled(ExperimentalHeadingContainer)<{
	onClick: React.ComponentProps<'h1' | 'div'>['onClick'];
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
	isPageContentFullWidth?: boolean;
	isPageContentMaxWidth?: boolean;
	isEmbeddedPage?: boolean;
	hasEmoji?: boolean;
	isFixedWidthImageOption?: boolean;
	isObjectHeaderEnabled?: boolean;
}>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&#title-text': {
			alignItems: 'center',
			font: token('font.heading.xlarge'),
			fontWeight: token('font.weight.regular'),
			flexDirection: ({
				isFixedWidthImageOption,
				hasEmoji,
				isPageContentFullWidth,
				hasCoverPicture,
				isTitleCenterAligned,
				isPageContentMaxWidth,
			}) =>
				isTitleCenterAligned ||
				(isFixedWidthImageOption &&
					hasEmoji &&
					!isTitleCenterAligned &&
					(isPageContentFullWidth ||
						(isPageContentMaxWidth &&
							(expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
								expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)))) &&
					hasCoverPicture)
					? 'column'
					: 'unset',
			marginTop: ({ isEmbeddedPage, hasCoverPicture, isObjectHeaderEnabled, hasEmoji }) =>
				getMarginTopStyledH1(isEmbeddedPage, hasCoverPicture, isObjectHeaderEnabled, hasEmoji),
			gap: ({ isTitleCenterAligned, hasCoverPicture }) =>
				isTitleCenterAligned && !hasCoverPicture ? token('space.200') : 'initial',
		},
		margin: '0',
		color: token('color.text'),
		wordBreak: 'break-word',
		wordWrap: 'break-word',
		whiteSpace: 'preserve',
		/* update next/packages/content-title/src/ContentTitlePlaceholder.js when this changes */
		minHeight: '35px',

		paddingTop: ({ isObjectHeaderEnabled }) => (isObjectHeaderEnabled ? '0px' : '5px'),
		cursor: 'pointer',
		display: 'flex',
		pointerEvents: ({ onClick }) => (onClick ? 'default' : 'none'),
	},
	({ isTitleCenterAligned }) =>
		isTitleCenterAligned
			? 'justify-content: center; text-align: center; flex-direction: column; align-items: center'
			: '',
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const StyledHeadingText = styled.h1({
	font: token('font.heading.xlarge'),
	fontWeight: token('font.weight.regular'),
	display: 'inline',
});

const emojiFullWidthLeftAlignWrapper = css({
	display: 'flex',
	justifyContent: 'initial',
	width: '800px',
	paddingLeft: token('space.500'),
});

const EmojiAlignmentWrapper = ({ children, isLeftAlignedTitleWithFixedWidthImageAndEmoji }) => (
	<>
		{isLeftAlignedTitleWithFixedWidthImageAndEmoji ? (
			<div css={emojiFullWidthLeftAlignWrapper} data-testid="emoji-alignment-wrapper">
				{children}
			</div>
		) : (
			<>{children}</>
		)}
	</>
);

const titleTextFullWidthLeftAlignWrapper = css({
	display: 'flex',
	justifyContent: 'flex-start',
	width: '100%',
});

const TitleTextAlignmentWrapper = ({ children, isLeftAlignedTitleWithFixedWidthImageAndEmoji }) => (
	<>
		{isLeftAlignedTitleWithFixedWidthImageAndEmoji ? (
			<div css={titleTextFullWidthLeftAlignWrapper} data-testid="title-alignment-wrapper">
				{children}
			</div>
		) : (
			<>{children}</>
		)}
	</>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const EmojiWrapper = styled.div<{
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
	isObjectHeaderEnabled?: boolean;
	isEmbeddedPage?: boolean;
}>({
	paddingRight: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? '0px' : '10px'),
	display: 'flex',
	justifyContent: 'center',
	maxWidth: ({ hasCoverPicture }) =>
		hasCoverPicture ? `${EMOJI_SIZE_LARGE}px` : `${EMOJI_SIZE_MEDIUM}px`,
	alignItems: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'initial' : 'center'),
	position: ({ hasCoverPicture }) => (hasCoverPicture ? 'absolute' : 'unset'),
	top: ({ hasCoverPicture, isObjectHeaderEnabled }) =>
		getEmojiTopOffsetValue(hasCoverPicture, isObjectHeaderEnabled),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> * span': {
		display: 'flex',
		justifyContent: 'center',
		width: ({ hasCoverPicture }) =>
			hasCoverPicture ? `${EMOJI_SIZE_LARGE}px` : `${EMOJI_SIZE_MEDIUM}px`,
		height: ({ hasCoverPicture }) =>
			hasCoverPicture ? `${EMOJI_SIZE_LARGE}px` : `${EMOJI_SIZE_MEDIUM}px`,
		overflow: 'hidden',
		alignItems: 'center',
	},
});

const titleCenteredMargin = (isTitleCenterAligned) => {
	return isTitleCenterAligned ? '-5px' : 'unset';
};

const getMarginTopTitleTextWrapper = (
	isTitleCenterAligned,
	hasCoverPicture,
	isObjectHeaderEnabled,
	hasEmoji,
	isEmbeddedPage,
) => {
	return isEmbeddedPage
		? '0px'
		: hasCoverPicture && hasEmoji && isObjectHeaderEnabled
			? '96px'
			: !isObjectHeaderEnabled && hasCoverPicture
				? '26px'
				: titleCenteredMargin(isTitleCenterAligned);
};
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const TitleTextWrapper = styled.span<{
	isEmbeddedPage?: boolean;
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
	isObjectHeaderEnabled?: boolean;
	hasEmoji?: boolean;
}>({
	marginTop: ({
		isTitleCenterAligned,
		hasCoverPicture,
		isObjectHeaderEnabled,
		hasEmoji,
		isEmbeddedPage,
	}) =>
		getMarginTopTitleTextWrapper(
			isTitleCenterAligned,
			hasCoverPicture,
			isObjectHeaderEnabled,
			hasEmoji,
			isEmbeddedPage,
		),
	/* Slide out and show "copy link" button next to title on hover */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover > span, &:focus-within > span': {
		opacity: '1',
		transform: 'none',
		transformDuration: '0.1s',
	},
});

const copyLinkWrapper = xcss({
	font: token('font.body'),
	opacity: '0',
	position: 'absolute',
	transform: 'translateX(-4px)',
	transition: 'opacity 0.2s ease-out 0s, transform 0.2s ease-out 0s',
});

type ContentTitleProps = {
	banner?: React.ReactNode | null;
	centerTitle: boolean;
	lookAndFeel?: {
		content: {
			header: any;
		};
		headings: any;
	};
	prefix?: string | '';
	title?: string | '';
	children?: React.ReactNode | null;
	hasEmoji: boolean;
	emojiId: string | '';
	contentId: string;
	contentType: string;
	spaceKey: string;
	loading: boolean;
	headingContainer?:
		| React.ComponentType<{
				children: React.ReactNode;
				showInlineCommentButton?: boolean;
		  }>
		| ReactComponentLike;
	hasCoverPicture?: boolean;
	isPageContentFullWidth?: boolean;
	isPageContentMaxWidth?: boolean;
	isEmbeddedPage?: boolean;
	titleContentProperties: TitleContentPropertiesType;
};

const isURLVisible = () => window.top === window.self;

export const ContentTitleComponent: FC<ContentTitleProps> = ({
	lookAndFeel,
	banner,
	title,
	children,
	centerTitle,
	prefix,
	hasEmoji,
	emojiId,
	contentId,
	spaceKey,
	contentType,
	loading,
	headingContainer: HeadingContainer,
	hasCoverPicture,
	isPageContentFullWidth,
	isPageContentMaxWidth,
	isEmbeddedPage,
	titleContentProperties,
}) => {
	'use no memo';
	const emojiTitle = hasEmoji ? emojiId : null;

	const { setQueryParams: routeActionsSetQueryParams } = useRouteActions();
	const routeDataRef = useRouteDataRef();

	const { coverPictureWidth } = useTitleContentPropertiesForPublishedPage({
		contentId,
	});
	const isFixedWidthImageOption = coverPictureWidth === COVERPICTUREWIDTH.FIXED;

	const isTitleCenterAligned =
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;

	/**
	 * The feature of clearing query params by clicking title is only useful when it's used in standalone Confluence with the URL visible.
	 * When a page gets embedded e.g. rendered within iframe macro, user won't be able to see the effect from clearing query params and the mouse pointer and clicking lead to a confusing user experience.
	 */
	const clearQueryParams = () => {
		if (!isURLVisible()) {
			return;
		}
		const queryParams = routeDataRef?.current?.queryParams;
		if (!queryParams) return;
		Object.keys(queryParams).forEach((k) => {
			routeActionsSetQueryParams({ [k]: undefined }, true);
		});
	};

	const isLeftAlignedTitleWithFixedWidthImageAndEmoji =
		isFixedWidthImageOption &&
		hasEmoji &&
		!isTitleCenterAligned &&
		hasCoverPicture &&
		(isPageContentFullWidth ||
			(isPageContentMaxWidth &&
				(expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
					expValEquals('confluence_max_width_content_appearance', 'isEnabled', true))));

	useTrackTitleRedactionRendered({ contentId, title });
	const { activeSpotlight } = useRedactionsTour({
		contentId,
		step: RedactionsTourSteps.FIRST,
		firstStepSource: RedactionTourStepSource.titleRedaction,
	});

	const headingId = 'heading-title-text';
	const heading = (
		<StyledHeadingContainer
			id="title-text"
			data-test-id="title-text"
			data-testid="title-text"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={lookAndFeel?.headings}
			onClick={clearQueryParams}
			isTitleCenterAligned={isTitleCenterAligned}
			hasCoverPicture={hasCoverPicture}
			isPageContentFullWidth={isPageContentFullWidth}
			isPageContentMaxWidth={isPageContentMaxWidth}
			isFixedWidthImageOption={isFixedWidthImageOption}
			isEmbeddedPage={isEmbeddedPage}
			hasEmoji={hasEmoji}
			isObjectHeaderEnabled={fg('confluence_frontend_content_wrapper')}
		>
			{emojiTitle && (
				<EmojiAlignmentWrapper
					isLeftAlignedTitleWithFixedWidthImageAndEmoji={
						isLeftAlignedTitleWithFixedWidthImageAndEmoji
					}
				>
					<EmojiWrapper
						isTitleCenterAligned={isTitleCenterAligned}
						hasCoverPicture={hasCoverPicture}
						isObjectHeaderEnabled={fg('confluence_frontend_content_wrapper')}
						isEmbeddedPage={isEmbeddedPage}
					>
						<Text testId="emoji-title">
							<LazyEmojiComponentLoader
								emoji={emojiTitle}
								height={hasCoverPicture ? EMOJI_SIZE_LARGE : EMOJI_SIZE_MEDIUM}
								hasCoverPicture={hasCoverPicture}
								context="viewPage"
								showTooltip
								renderResourcedEmoji
								isPageTitleComponent
							/>
						</Text>
					</EmojiWrapper>
				</EmojiAlignmentWrapper>
			)}
			<TitleTextAlignmentWrapper
				isLeftAlignedTitleWithFixedWidthImageAndEmoji={
					isLeftAlignedTitleWithFixedWidthImageAndEmoji
				}
			>
				<SpotlightManager>
					<SpotlightTarget name={REDACTIONS_TOUR_TARGETS.TITLE_REDACTION}>
						{(() => {
							// Extract title content to avoid duplication
							const titleContent = (
								<TitleTextWrapper
									isTitleCenterAligned={isTitleCenterAligned}
									hasCoverPicture={hasCoverPicture}
									hasEmoji={hasEmoji}
									isObjectHeaderEnabled={fg('confluence_frontend_content_wrapper')}
									isEmbeddedPage={isEmbeddedPage}
									id="content-title-id" // this id is required for useIsTitleIntersectingWithHeader
								>
									<StyledHeadingText id={headingId}>
										{prefix ? `${prefix} - ${title}` : title}
									</StyledHeadingText>
									<Box
										as="span"
										paddingInlineStart="space.100"
										xcss={copyLinkWrapper}
										data-vc="copylink-wrapper"
										testId="copylink-wrapper"
									>
										<CopyLinkButtonLoader
											shouldResetOnHover
											contentId={contentId}
											spaceKey={spaceKey}
											contentType={contentType}
											pageMode="view"
											source="pageTitle"
											buttonAppearance="subtle-link"
											tooltipPosition="top"
											referencedContentId={headingId}
										/>
									</Box>
								</TitleTextWrapper>
							);

							return titleContent;
						})()}
					</SpotlightTarget>
					<SpotlightTransition>{activeSpotlight}</SpotlightTransition>
				</SpotlightManager>
			</TitleTextAlignmentWrapper>
		</StyledHeadingContainer>
	);

	const headerStyles = {
		...lookAndFeel?.content?.header,
	};
	// header is from graphql result which will contain __typename
	delete headerStyles.__typename;

	return (
		<div
			css={[
				!centerTitle && isPageContentFullWidth && fullWidthLeftAlignStyles,
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				!centerTitle &&
					isPageContentMaxWidth &&
					(expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
						expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)) &&
					maxWidthLeftAlignStyles,
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				isEmbeddedPage &&
					expValEquals('cc_page_experiences_native_embed_ui_updates', 'isEnabled', true) &&
					embeddedNativeUITitlePaddingStyles,
			]}
			data-testid="title-wrapper"
		>
			<div
				css={[styledContentTitle, centerTitle && centerTitleStyles]}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				style={headerStyles}
				data-test-id="page-content-header"
				data-testid="page-content-header"
			>
				<HeadingWrapper hasBanner={!!banner} isEmbeddedPage={isEmbeddedPage}>
					{HeadingContainer ? (
						<HeadingContainer showInlineCommentButton>{heading}</HeadingContainer>
					) : (
						heading
					)}
				</HeadingWrapper>
				{banner || null}
				{!loading && <PageSegmentLoadEnd key={contentId} metric={CONTENT_TITLE_METRIC} />}
				{children}
				<ExperienceSuccess name={VIEW_PAGE_TITLE_EXPERIENCE} />
			</div>
		</div>
	);
};
ContentTitleComponent.displayName = 'ContentTitleComponent';
